import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { z } from 'zod';
import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { queryKeysFtpPoller } from './queryKeys';

type DeleteFtpPollerResponse = {
  code: number;
  message: string;
  requestId: UUID | undefined;
};

const zodDeleteFtpPollerEndpointSchema = z.tuple([
  z.literal('vadmin'),
  z.literal('ftp_poller'),
  z.literal('secret'),
  z.uuid(),
]);

/**
 * Delete a FTP poller secret with a given ID.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-FTP-Secrets/operation/delete_ftp_secret_by_id_vadmin_ftp_poller_secret__ftp_id__delete
 */
export const deleteFtpPoller = async (ftpPollerId: UUID) => {
  try {
    const response = await vestigasApi.delete(
      ENDPOINT.FTP_POLLER.DELETE(ftpPollerId),
      { validate: { pathSegments: zodDeleteFtpPollerEndpointSchema } },
    );

    return response;
  } catch (error) {
    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.mutation.deleteFtpPoller.error422, {
            ns: 'ftpPoller',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.mutation.deleteFtpPoller.error.generic,
            {
              ns: 'ftpPoller',
            },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * A custom hook to delete a FTP poller secret with a given ID.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-FTP-Secrets/operation/delete_ftp_secret_by_id_vadmin_ftp_poller_secret__ftp_id__delete
 */
export const useMutationDeleteFtpPoller = (
  options?: UseMutationOptions<unknown, unknown, UUID>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ftpPollerId: UUID) => deleteFtpPoller(ftpPollerId),
    onError(_error) {
      return; // prevent error from bubbling up further unless overridden via mutation options
    },
    onSettled(_data, _error) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysFtpPoller.getAll({}),
      });
    },
    onSuccess() {
      ToastService.info(
        i18n.t(($) => $.mutation.deleteFtpPoller.success, { ns: 'ftpPoller' }),
      );
    },
    ...options,
  });
};
